import {
  useState,
  useEffect,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { compose } from 'core/libs/recompose';
import { withRouter } from 'core/libs/router';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import themePropTypes from 'core/utils/prop-types/theme';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';
import withUser from 'core/hocs/withUser';

import Dropdown from 'core/components/Dropdown';
import Link from 'core/components/Link';
import NotifyIcon from 'core/components/ugc/NotifyIcon';
import Logout from 'core/components/Logout';

import LoginButton from './LoginButton';
import UserInfoProfile from './UserInfoProfile';
import LoginPopup from './LoginPopup';

import styles from './index.styl';
import openAuth from './utils.js';


function Auth(props, context) {
  const {
    theme,
    theme: {
      icons: { ArrowDropdown },
      colors,
      controls: {
        shapka: shapkaStyles,
      },
    },
    location: {
      pathname,
    },
    isMobile,
    user,
    asDropdown,
    showPopUp,
  } = props;

  const [dropdownOpened, setDropdownOpened] = useState(false);

  useEffect(() => {
    setDropdownOpened(false);
  }, [pathname]);

  const arrowSize = !isMobile ? 10 : 6;

  const scope = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .logout
          color ${colors.error}
          &:visited
            color ${colors.error}

        .${styles.menuItem}
          text-decoration none
      `}</style>
    </scope>
  );

  if (showPopUp) {
    return <LoginPopup handleAuth={() => openAuth(context)} />;
  }

  if (!user.isAuthorized) {
    return (
      <LoginButton
        handleAuth={() => openAuth(context)}
        hidden={!user.resolved}
      />
    );
  }

  const linksMenu = [
    {
      path: '/profile',
      text: 'Мой профиль',
      conditionRendering: true,
    },
    {
      path: '/profile/topic/new',
      text: 'Написать статью',
      conditionRendering: true,
    },
    {
      path: '/profile/topics',
      text: 'Список моих материалов',
      icon: user.notify && <NotifyIcon />,
      conditionRendering: true,
    },
    {
      path: '/profile/settings',
      text: 'Настройки авторства',
      conditionRendering: true,
    },
  ];

  const anchorElement = (
    <span className={cx(styles.wrapperInfo, asDropdown && styles._asDropdown)}>
      <UserInfoProfile
        user={user}
        theme={theme}
        location={location}
        isDesktop={!isMobile}
        asDropdown={asDropdown}
      />
      {user.notify && <NotifyIcon className={styles.avatarNotify} />}
      {asDropdown &&
        <ArrowDropdown
          width={arrowSize}
          height={arrowSize}
          className={cx(styles.arrow, dropdownOpened && styles._opened)}
        />
      }
    </span>
  );

  if (!asDropdown) return anchorElement;

  return (
    <div className={styles.user}>
      <style jsx>{`
        .${styles.user}
          font ${shapkaStyles.link.font}
          text-transform ${shapkaStyles.link.textTransform}
          letter-spacing ${shapkaStyles.link.letterSpacing}
          color ${shapkaStyles.link.idle.color}
          text-decoration ${shapkaStyles.link.idle.decoration}
      `}
      </style>
      <Dropdown
        anchor={anchorElement}
        contentPosition='right'
        showIcon={false}
        onOpen={() => setDropdownOpened(true)}
        onClose={() => setDropdownOpened(false)}
        key={pathname}
      >
        <div className={styles.menu}>
          {linksMenu.map(({ path, text, icon, conditionRendering }) => {
            return (
              conditionRendering &&
                <Link
                  to={path}
                  className={scope.wrapClassNames(styles.menuItem)}
                  key={path}
                >
                  {text}{icon && <Fragment>&nbsp;{icon}</Fragment>}
                </Link>
            );
          })}
          <Logout
            className={scope.wrapClassNames(styles.menuItem, 'logout')}
          />
        </div>
      </Dropdown>
      <scope.styles />
    </div>
  );
}

Auth.contextTypes = {
  host: PropTypes.string,
  ramblerId: PropTypes.object,
};

Auth.propTypes = {
  /** @ignore */
  isMobile: PropTypes.bool,
  /**
    * Показываем выпадающее меню с опциями для пользователя
   */
  asDropdown: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  /** @ignore */
  theme: themePropTypes(`{
    icons: {
      ArrowDropdown
    },
    controls: {
      shapka: {
        link: {
          font,
          textTransform,
          letterSpacing,
          idle: {
            color,
            decoration,
          },
        },
      }
    },
    colors: {
      error,
    }
  }`),
  /**
   * Данные о пользователе в Rambler ID
   */
  user: PropTypes.shape({
    isAuthorized: PropTypes.bool,
    notify: PropTypes.bool,
    userInfo: PropTypes.object,
    resolved: PropTypes.bool,
  }),
  showPopUp: PropTypes.bool,
};

const AuthWithHOCs = compose(
  withRouter,
  withBreakpoint,
  withTheme,
  withUser,
)(Auth);
AuthWithHOCs.displayName = 'Auth';

export { Auth as StorybookComponent };
export default AuthWithHOCs;
